
import { defineComponent } from '@vue/composition-api';
import {
  BetOutcomeModal,
  SuccessBetOutcomeModal,
  BookedBetOutcomeModal,
  ErrorBetOutcomeModal,
  ReviewBetOutcomeModal
} from '../../../../../types/sport';
import IllinoisIcons from '../icons.vue';
import BetInAcceptance from './BetInAcceptance.vue';

export default defineComponent({
  name: 'BetOutcomeModal',
  components: { IllinoisIcons, BetInAcceptance },
  data() {
    return {
      copied: false,
      copyTimer: undefined as number | undefined,
      copiedCodes: {} as Record<string, boolean>,
      copyTimers: {} as Record<string, number | undefined>
    };
  },
  computed: {
    betOutcome(): BetOutcomeModal {
      return this.$store.getters['IllinoisSport/getBetOutcome'];
    },
    successOutcome(): SuccessBetOutcomeModal | null {
      if (this.betOutcome?.type === 'success' && this.betOutcome.outcome) {
        return this.betOutcome.outcome as SuccessBetOutcomeModal;
      }
      return null;
    },
    bookedOutcome(): BookedBetOutcomeModal | null {
      if (this.betOutcome?.type === 'booked' && this.betOutcome.outcome) {
        return this.betOutcome.outcome as BookedBetOutcomeModal;
      }
      return null;
    },
    errorOutcome(): ErrorBetOutcomeModal | null {
      if (this.betOutcome?.type === 'error' && this.betOutcome.outcome) {
        return this.betOutcome.outcome as ErrorBetOutcomeModal;
      }
      return null;
    },
    reviewOutcome(): ReviewBetOutcomeModal | null {
      if (this.betOutcome?.type === 'review' && this.betOutcome.outcome) {
        return this.betOutcome.outcome as ReviewBetOutcomeModal;
      }
      return null;
    },
    translatedErrorCode(): string {
      const code = this.errorOutcome?.error_code;
      if (!code) return '';
      // Traduce la chiave i18n
      return this.t(code);
    },
    translatedErrorReason(): string {
      const reason = this.errorOutcome?.reason;
      if (!reason || reason === 'n.d.') return '';
      // Lista di chiavi i18n note - se reason è una di queste, traduci
      const i18nKeys = [
        'pleaseTryAgain',
        'vincols_repetitions',
        'betUnderReview',
        'oddNotAvailableInCart',
        'errors.user_locked_text',
        'agency_locked',
        'errors.MAX_CURRENT_WEEK_PLAYABLE',
        'errors.MAX_CURRENT_DAY_PLAYABLE',
        'marketClosedPleaseTryAgain',
        'errors.user_staged_text',
        'alertlogin',
        'rejectedByAi',
        'errors.userProfileNotEnabled'
      ];
      // Se reason è una chiave i18n nota, traduci
      if (i18nKeys.includes(reason) || reason.startsWith('errors.')) {
        return this.t(reason);
      }
      // Altrimenti restituisci il testo diretto (es. per external_save_failed con reason personalizzato)
      return reason;
    },
    getPlatformSettings(): any {
      return this.$store.getters['settings/getPlatformSettings'];
    },
    getOddsType(): string {
      return this.$store.getters['odds/getOddsType'];
    },
    getType(): number {
      return this.$store.getters['user/getType'];
    },
    getId(): string | number {
      return this.$store.getters['user/getId'];
    },
    getShortCode(): boolean {
      return this.$store.getters['settings/getShortCode'];
    },
    betCodes(): Array<{ code: string; label: string; key: string }> {
      if (!this.successOutcome) return [];
      const codes: Array<{ code: string; label: string; key: string }> = [];
      if (this.successOutcome.short_code) {
        codes.push({
          code: this.successOutcome.short_code,
          label: 'Short Code',
          key: 'short_code'
        });
      }
      if (this.successOutcome.bet_code) {
        codes.push({
          code: this.successOutcome.bet_code,
          label: 'Bet Code',
          key: 'bet_code'
        });
      }
      return codes;
    }
  },
  watch: {
    betOutcome: {
      handler(newVal) {
        if (newVal && newVal.open === true) {
          // Chiudi il carrello quando si apre la modale
          this.$store.dispatch('cart/setIsCartOpen', false);
        }
      },
      immediate: true
    }
  },
  methods: {
    t(key: string): string {
      return this.$t(key) as string;
    },
    handleClose() {
      if (this.successOutcome) {
        this.$store.dispatch('cart/resetCart');
      }
      this.$store.dispatch('IllinoisSport/clearBetOutcome');
    },
    handleNewBet() {
      this.$store.dispatch('IllinoisSport/clearBetOutcome');
      this.$store.dispatch('cart/resetCart');
    },
    handleReloadBet() {
      this.$store.dispatch('IllinoisSport/clearBetOutcome');
    },
    handlePrint() {
      const code = this.successOutcome?.short_code;
      if (!code) return;

      const printEndpoint = this.getPlatformSettings?.print_endpoint;
      if (!printEndpoint) {
        console.warn('Print endpoint not configured');
        return;
      }

      let url = printEndpoint + code;
      if (this.getShortCode) {
        url += '&login=' + this.getType + '&ancestors=' + this.getId;
      }
      url += '&type_odd=' + this.getOddsType;

      window.open(url, '_blank', 'height=600,width=350');
    },
    async copyToClipboard(text: string, codeKey?: string) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          // Fallback per browser che non supportano clipboard API
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.setAttribute('readonly', '');
          textarea.style.position = 'absolute';
          textarea.style.left = '-9999px';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }

        // Se codeKey è fornito, gestisci il feedback individuale per quel codice
        if (codeKey) {
          this.$set(this.copiedCodes, codeKey, true);
          // Pulisci il timer precedente se esiste
          if (this.copyTimers[codeKey]) {
            window.clearTimeout(this.copyTimers[codeKey]);
          }
          // Imposta il timer per resettare lo stato dopo 2 secondi
          this.copyTimers[codeKey] = window.setTimeout(() => {
            this.$set(this.copiedCodes, codeKey, false);
            this.copyTimers[codeKey] = undefined;
          }, 2000);
        } else {
          // Mantieni il comportamento originale per retrocompatibilità
          this.copied = true;
          if (this.copyTimer) window.clearTimeout(this.copyTimer);
          this.copyTimer = window.setTimeout(() => {
            this.copied = false;
          }, 3000);
        }
      } catch (e) {
        console.error('Failed to copy:', e);
      }
    }
  },
  beforeDestroy() {
    if (this.copyTimer) {
      window.clearTimeout(this.copyTimer);
    }
    // Pulisci tutti i timer dei codici multipli
    Object.values(this.copyTimers).forEach(timer => {
      if (timer !== undefined) {
        window.clearTimeout(timer as number);
      }
    });
  }
});
